import { queryOptions, replaceEqualDeep, type QueryClient } from '@tanstack/react-query'
import type { QuoteBuilderApi } from './client'
import type { Customer, Quote, QuoteWorkflow } from './types'

// Query keys follow the demo so every stage shares one cache entry per read:
// ['quotes'], ['quote', id], ['quote-workflow', id], ['customer-search', q].

/** Keeps the cached copy when a slower response carries an older revision. */
export function keepNewestRevision<T>(previous: unknown, incoming: T): T {
  if (
    previous &&
    incoming &&
    typeof previous === 'object' &&
    typeof incoming === 'object' &&
    'revision' in previous &&
    'revision' in incoming &&
    typeof previous.revision === 'number' &&
    typeof incoming.revision === 'number' &&
    previous.revision > incoming.revision
  ) {
    return previous as T
  }
  return replaceEqualDeep(previous, incoming) as T
}

/** Same rule for stage reads that carry `quote_revision` (services, taxes, financing, …). */
export function retainNewerStageRead(previous: unknown, next: unknown) {
  const revision = (value: unknown) => {
    const data = value as { revision?: number; quote_revision?: number } | undefined
    return data?.quote_revision ?? data?.revision ?? 0
  }
  return revision(previous) > revision(next) ? previous : replaceEqualDeep(previous, next)
}

export const quotesQueryOptions = (api: QuoteBuilderApi) =>
  queryOptions({
    queryKey: ['quotes'],
    queryFn: ({ signal }) => api.request<Quote[]>('/quotes', { signal }),
  })

export function normalizeCustomerSearch(query: string) {
  return query.trim().replace(/\s+/g, ' ')
}

export const customerSearchQueryOptions = (api: QuoteBuilderApi, query: string) =>
  queryOptions({
    queryKey: ['customer-search', query],
    queryFn: ({ signal }) => {
      const search = new URLSearchParams({ limit: '25' })
      if (query) search.set('q', query)
      return api.request<Customer[]>(`/customers?${search}`, { signal })
    },
  })

export const quoteQueryOptions = (api: QuoteBuilderApi, id: string) =>
  queryOptions({
    queryKey: ['quote', id],
    queryFn: ({ signal }) => api.request<Quote>(`/quotes/${id}`, { signal }),
    structuralSharing: keepNewestRevision,
    enabled: Boolean(id),
  })

export const workflowQueryOptions = (api: QuoteBuilderApi, id: string) =>
  queryOptions({
    queryKey: ['quote-workflow', id],
    queryFn: ({ signal }) => api.request<QuoteWorkflow>(`/quotes/${id}/workflow`, { signal }),
    structuralSharing: keepNewestRevision,
    enabled: Boolean(id),
  })

/** Refresh My Quotes after a quote is created, changed or published. */
export async function invalidateQuoteOverview(client: QueryClient): Promise<void> {
  await client.invalidateQueries({ queryKey: ['quotes'] })
}

/**
 * Apply a saved quote to the cache the way the demo's workspace does: never step back to an older
 * revision, and refetch the workflow when the quote moved past it.
 */
export function applyQuoteChange(client: QueryClient, quoteId: string, next: Quote) {
  if (next.id !== quoteId) return
  void client.cancelQueries({ queryKey: ['quote', quoteId] })
  client.setQueryData<Quote>(['quote', quoteId], (current) =>
    current && current.revision > next.revision ? current : next,
  )
  const workflow = client.getQueryData<QuoteWorkflow>(['quote-workflow', quoteId])
  if (!workflow || workflow.revision < next.revision) {
    void client.invalidateQueries({ queryKey: ['quote-workflow', quoteId] }, { cancelRefetch: false })
  }
}

export function applyWorkflowChange(client: QueryClient, quoteId: string, next: QuoteWorkflow) {
  if (next.quote_id !== quoteId) return
  void client.cancelQueries({ queryKey: ['quote-workflow', quoteId] })
  client.setQueryData<QuoteWorkflow>(['quote-workflow', quoteId], (current) =>
    current && current.revision > next.revision ? current : next,
  )
}
